import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Wrench, Home, Rocket, ArrowLeft } from "lucide-react";
import AppShell from "@/components/AppShell";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";

// Per-role list of what the user can post
const ROLE_POST_TYPES = {
  farmer: [
    { key: "product",  label: "Product",  desc: "Sell your crop, seed, milk, honey…",       Icon: Package },
    { key: "service",  label: "Service",  desc: "Offer tractor, ploughing, harvesting…",    Icon: Wrench },
    { key: "property", label: "Property", desc: "Sell or rent land, farm, plot, house…",    Icon: Home },
  ],
  day_worker: [
    { key: "service",  label: "Service",  desc: "Driver, mason, painter, electrician…",     Icon: Wrench },
    { key: "product",  label: "Product",  desc: "Anything you'd like to sell",              Icon: Package },
    { key: "property", label: "Property", desc: "Sell or rent land, shop, house…",          Icon: Home },
  ],
  investor: [
    { key: "project",  label: "Project / Investment",  desc: "Find investor, producer, partner, funding…", Icon: Rocket },
    { key: "product",  label: "Product",  desc: "Anything to sell",                          Icon: Package },
    { key: "service",  label: "Service",  desc: "Offer a professional service",              Icon: Wrench },
    { key: "property", label: "Property", desc: "Land, plot, house, shop, building…",        Icon: Home },
  ],
};

export default function CreatePostChooser() {
  const nav = useNavigate();
  const { user } = useAuth();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const options = ROLE_POST_TYPES[user?.role] || ROLE_POST_TYPES.farmer;

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-3xl">
        <button data-testid="chooser-back" onClick={() => nav(-1)} className="text-sm text-gray-500 flex items-center gap-1"><ArrowLeft className="h-4 w-4" />Back</button>
        <div className="mt-6 text-xs uppercase tracking-[0.24em] text-gray-500">Create post</div>
        <h1 className="mt-2 text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">What do you want to post?</h1>
        <p className="mt-2 text-sm text-gray-600">You're posting as a <b className={theme.text}>{theme.name}</b>. Pick the type that fits — you can always create more later.</p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {options.map(({ key, label, desc, Icon }, idx) => (
            <motion.button
              key={key}
              data-testid={`chooser-${key}`}
              onClick={() => nav(`/post/${key}`)}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
              whileHover={{ y: -3 }}
              className={`group relative overflow-hidden text-left rounded-3xl bg-white border ${theme.border} p-6 premium-shadow hover:shadow-2xl transition-shadow`}>
              <div className={`h-12 w-12 rounded-2xl ${theme.bg} text-white flex items-center justify-center`}>
                <Icon className="h-6 w-6" />
              </div>
              <div className="mt-4 text-lg font-bold text-gray-900">{label}</div>
              <div className="mt-1 text-sm text-gray-600">{desc}</div>
              <div className={`absolute inset-x-0 bottom-0 h-1 ${theme.bg} scale-x-0 group-hover:scale-x-100 origin-left transition-transform`} />
            </motion.button>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
